import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of, retry, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { NewsArticle, NewsItem } from '../models/news-item';

/** Akkor dobjuk, ha a slug nem szerepel az index.json-ban – így a UI „nem található”-t tud mutatni. */
export class NewsNotFoundError extends Error {
  constructor(public readonly slug: string) {
    super(`Nincs ilyen hír: ${slug}`);
    this.name = 'NewsNotFoundError';
  }
}

const NEWS_BASE = '/assets/news';

@Injectable({ providedIn: 'root' })
export class NewsService {
  private readonly http = inject(HttpClient);

  /** Gyorsítótárazott index. Hiba esetén nullázzuk, hogy az újratöltés valóban újrapróbálkozzon. */
  private indexCache: Observable<NewsItem[]> | null = null;

  /** Az összes hír, legújabb elöl. */
  list(): Observable<NewsItem[]> {
    this.indexCache ??= this.http.get<NewsItem[]>(`${NEWS_BASE}/index.json`).pipe(
      map(items =>
        (items ?? [])
          .filter(item => !!item.slug && !!item.title)
          // A dátum ISO formátumú, ezért a szöveges összehasonlítás egyben idősorrend is.
          .sort((a, b) => b.date.localeCompare(a.date))
      ),
      retry({ count: 1, delay: 400 }),
      // A shareReplay a hibát is megjegyzi, ezért előtte eldobjuk a cache-t –
      // különben egyetlen sikertelen kérés után az újratöltés örökre ugyanazt a hibát adná.
      tap({ error: () => (this.indexCache = null) }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.indexCache;
  }

  /** Egy hír metaadata, vagy NewsNotFoundError. */
  item(slug: string): Observable<NewsItem> {
    return this.list().pipe(
      map(items => items.find(item => item.slug === slug)),
      switchMap(item => (item ? of(item) : throwError(() => new NewsNotFoundError(slug))))
    );
  }

  /** Egy hír metaadata + HTML törzse. */
  article(slug: string): Observable<NewsArticle> {
    return this.item(slug).pipe(
      switchMap(meta =>
        // A slug itt már biztosan szerepel az index.json-ban, így nem lehet vele
        // más fájlra kimutatni (path traversal).
        this.http.get(`${NEWS_BASE}/${meta.slug}.html`, { responseType: 'text' }).pipe(
          switchMap(body =>
            isSpaFallback(body)
              ? throwError(() => new NewsNotFoundError(slug))
              : of<NewsArticle>({ meta, body })
          )
        )
      )
    );
  }
}

/**
 * Az nginx `try_files ... /index.html` miatt egy hiányzó törzsfájl nem 404-cel, hanem
 * a teljes SPA-vázzal és HTTP 200-cal jön vissza. Ezt itt szűrjük ki, különben az
 * index.html tartalma kerülne a cikk törzsébe.
 */
function isSpaFallback(body: string): boolean {
  const head = body.slice(0, 400).toLowerCase();
  return head.includes('<!doctype html') || head.includes('<app-root');
}
